import { useState, useCallback, useRef } from 'react';

let toastIdCounter = 0;

export const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const timeoutsRef = useRef({});

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    if (timeoutsRef.current[id]) {
      clearTimeout(timeoutsRef.current[id]);
      delete timeoutsRef.current[id];
    }
  }, []);

  const addToast = useCallback(({
    type = 'info',
    title,
    message,
    duration = 5000
  }) => {
    const id = ++toastIdCounter;
    
    const toast = {
      id,
      type,
      title,
      message,
      duration: type === 'loading' ? 0 : duration
    };

    setToasts((prev) => [...prev, toast]);

    if (duration && type !== 'loading') {
      timeoutsRef.current[id] = setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const toast = {
    success: (title, message, duration) => 
      addToast({ type: 'success', title, message, duration }),
    
    error: (title, message, duration) => 
      addToast({ type: 'error', title, message, duration }),
    
    warning: (title, message, duration) => 
      addToast({ type: 'warning', title, message, duration }),
    
    info: (title, message, duration) => 
      addToast({ type: 'info', title, message, duration }),
    
    loading: (title, message) => 
      addToast({ type: 'loading', title, message, duration: 0 }),
    
    dismiss: (id) => removeToast(id),
    
    promise: async (promise, messages) => {
      const loadingId = addToast({
        type: 'loading',
        title: messages.loading || 'Processing...',
        duration: 0
      });

      try {
        const result = await promise;
        removeToast(loadingId);
        addToast({
          type: 'success',
          title: messages.success || 'Success!',
          duration: 5000
        });
        return result;
      } catch (error) {
        removeToast(loadingId);
        addToast({
          type: 'error',
          title: messages.error || 'Error occurred',
          message: error.message,
          duration: 7000
        });
        throw error;
      }
    }
  };

  return { toasts, toast, removeToast };
};
